import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { useFloating } from '../../hooks/useFloating';
import { useControllable } from '../../hooks/useControllable';
import styles from './ColorPicker.module.css';

const colorPicker = cva(styles.wrapper);
const swatchCva = cva(styles.swatch, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
  },
  defaultVariants: { size: 'md' },
});

type SwatchVariants = VariantProps<typeof swatchCva>;

type ColorFormat = 'hex' | 'rgb';

// ── HSV ↔ Hex helpers ─────────────────────────────────────────────────────────

function hexToHsv(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [Math.round(h * 360), max === 0 ? 0 : Math.round((d / max) * 100), Math.round(max * 100)];
}

function hsvToHex(h: number, s: number, v: number): string {
  const sn = s / 100, vn = v / 100;
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return vn - vn * sn * Math.max(0, Math.min(k, 4 - k, 1));
  };
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`;
}

// ── Gradient canvas (saturation × brightness) ─────────────────────────────────

function GradientCanvas({ hue, saturation, brightness, onChange }: {
  hue: number; saturation: number; brightness: number;
  onChange: (s: number, v: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const dragging = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; });

  const getPos = useCallback((e: MouseEvent | React.MouseEvent) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const s = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height));
    onChangeRef.current(Math.round(s * 100), Math.round(v * 100));
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => { if (dragging.current) getPos(e); };
    const onUp = () => { dragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [getPos]);

  return (
    <div
      ref={ref}
      data-testid="color-gradient"
      onMouseDown={e => { dragging.current = true; getPos(e); }}
      className={styles.gradientCanvas}
      style={{
        '--gradient-hue': hue,
        '--thumb-left': `${saturation}%`,
        '--thumb-top': `${100 - brightness}%`,
        '--thumb-color': hsvToHex(hue, saturation, brightness),
      } as React.CSSProperties}
    >
      {/* White → transparent (saturation) */}
      <div className={styles.gradientWhite} />
      {/* Transparent → black (brightness) */}
      <div className={styles.gradientBlack} />
      {/* Thumb */}
      <div className={styles.gradientThumb} />
    </div>
  );
}

interface ColorPreset {
  label: string;
  colors: string[];
}

interface ColorPickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled value (hex string) */
  value?: string;
  /** Default value */
  defaultValue?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Color format */
  format?: ColorFormat;
  /** Show hex/rgb text */
  showText?: boolean;
  /** Size */
  size?: SwatchVariants['size'];
  /** Disabled */
  disabled?: boolean;
  /** Preset color groups */
  presets?: ColorPreset[];
  /** Show clear button */
  allowClear?: boolean;
}

const defaultPresets: ColorPreset[] = [
  {
    label: 'Recommended',
    colors: [
      '#F5222D',
      '#FA8C16',
      '#FADB14',
      '#52C41A',
      '#13C2C2',
      '#1677FF',
      '#722ED1',
      '#EB2F96',
      '#2D3134',
      '#8C8C8C',
    ],
  },
];

/**
 * ColorPicker — color selection with presets and hex input.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | string | — | Controlled hex color |
 * | defaultValue | string | '#1677FF' | Initial color |
 * | onChange | (value) => void | — | Change handler |
 * | format | 'hex' \| 'rgb' | 'hex' | Display format |
 * | showText | boolean | true | Show color text |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Trigger size |
 * | disabled | boolean | false | Disable |
 * | presets | ColorPreset[] | default set | Preset groups |
 * | allowClear | boolean | false | Show clear |
 *
 * @example
 * <ColorPicker defaultValue="#52C41A" />
 * <ColorPicker presets={[{ label: 'Brand', colors: ['#1677FF', '#722ED1'] }]} />
 */
export function ColorPicker({
  value: controlledValue,
  defaultValue = '#1677FF',
  onChange,
  format = 'hex',
  showText = true,
  size = 'md',
  disabled = false,
  presets = defaultPresets,
  allowClear: _allowClear = false,
  className,
  style,
  ...props
}: ColorPickerProps) {
  const [color, setColor] = useControllable(controlledValue, defaultValue, onChange);
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(color);

  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(color);
  const [derivedHue, saturation, brightness] = isValidHex ? hexToHsv(color) : [0, 0, 100];

  // Hue is stored separately so it survives round-trips through black/white
  // (hexToHsv returns hue=0 for any achromatic color, which would snap the canvas).
  const [hue, setHue] = useState(derivedHue);
  const hueRef = useRef(hue);

  const handleHsvChange = useCallback((h: number, s: number, v: number) => {
    const hex = hsvToHex(h, s, v);
    setColor(hex);
    setHexInput(hex);
    // Only persist hue when the color is chromatic — achromatic colors have no meaningful hue
    if (s > 0 && v > 0) {
      hueRef.current = h;
      setHue(h);
    }
  }, [setColor]);

  const handleGradientChange = useCallback((s: number, v: number) => {
    handleHsvChange(hueRef.current, s, v);
  }, [handleHsvChange]);

  const { triggerRef, floatingRef, position } = useFloating<HTMLDivElement, HTMLDivElement>({
    placement: 'bottom-start',
    offset: 8,
    open,
  });

  useEffect(() => {
    setHexInput(color);
  }, [color]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (floatingRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, triggerRef, floatingRef]);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setHexInput(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      setColor(val);
    }
  };

  const formatDisplay = (c: string) => {
    if (format === 'rgb') {
      const r = parseInt(c.slice(1, 3), 16);
      const g = parseInt(c.slice(3, 5), 16);
      const b = parseInt(c.slice(5, 7), 16);
      return `rgb(${r}, ${g}, ${b})`;
    }
    return c.toUpperCase();
  };

  return (
    <div className={colorPicker({ className })} style={style} {...props}>
      <div
        ref={triggerRef}
        onClick={() => !disabled && setOpen(!open)}
        className={clsx(swatchCva({ size }), open && styles.swatchOpen, disabled && styles.swatchDisabled)}
        style={{ '--swatch-color': color } as React.CSSProperties}
      />
      {showText && (
        <span className={styles.colorText}>
          {formatDisplay(color)}
        </span>
      )}

      <AnimatePresence>
        {open && (
          <Portal>
            <motion.div
              ref={floatingRef}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: duration.normal / 1000 }}
              className={styles.dropdown}
              data-floating-panel
              style={{ '--colorpicker-top': `${position.top}px`, '--colorpicker-left': `${position.left}px` } as React.CSSProperties}
            >
            {/* Gradient canvas */}
            <GradientCanvas
              hue={hue}
              saturation={saturation}
              brightness={brightness}
              onChange={handleGradientChange}
            />

            {/* Hue slider */}
            <input
              data-testid="hue-slider"
              type="range"
              min={0}
              max={360}
              value={hue}
              onChange={e => handleHsvChange(Number(e.target.value), saturation, brightness)}
              className={styles.hueSlider}
            />

            {/* Current color preview */}
            <div className={styles.previewRow}>
              <div
                className={styles.previewSwatch}
                style={{ '--preview-color': color } as React.CSSProperties}
              />
              <input
                value={hexInput}
                onChange={handleHexChange}
                className={styles.hexInput}
                placeholder="#000000"
              />
            </div>

            {/* Presets */}
            {presets.map((preset, pi) => (
              <div key={pi} className={styles.presetGroup}>
                <div className={styles.presetLabel}>
                  {preset.label}
                </div>
                <div className={styles.presetSwatches}>
                  {preset.colors.map((c) => (
                    <div
                      key={c}
                      onClick={() => {
                        setColor(c);
                        setHexInput(c);
                      }}
                      className={clsx(styles.presetSwatch, c === color && styles.presetSwatchActive)}
                      style={{ '--swatch-color': c } as React.CSSProperties}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            ))}
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </div>
  );
}
